import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, CheckCircle } from 'lucide-react';
import '../style/products/mfg.css'; // Import the CSS file
import Header from '@/common/Header';
import Footer from '@/common/Footer';

import logo from '../img/quanto_light.png'

export default function QuantoMfg() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const [hoveredBenefit, setHoveredBenefit] = useState(null);

  const benefits = [
    { title: "End-to-End Production Management", description: "Oversee the entire production lifecycle, from raw material sourcing to finished goods, ensuring complete visibility and control at every stage." },
    { title: "Real-Time Inventory Control", description: "Monitor raw materials, work-in-progress, and finished goods in real-time to minimize waste and maintain optimal stock levels." },
    { title: "Cost Optimization", description: "Track and manage production costs continuously to control expenses and boost profitability through efficient resource utilization." },
    { title: "Enhanced Quality Control", description: "Implement integrated quality checks throughout the production process to maintain high standards and reduce defects." },
    { title: "Seamless Supply Chain Management", description: "Coordinate suppliers, production schedules, and logistics efficiently to ensure timely deliveries and minimize delays." },
    { title: "Order and Job Management", description: "Handle orders and job assignments effectively, improving planning and resource allocation with complete visibility into order statuses." },
    { title: "Integrated Financial Management", description: "Link manufacturing operations with financial modules to automate accounting, invoicing, and compliance, providing a unified view of your business finances." },
    { title: "Production Planning and Scheduling", description: "Optimize production timelines with advanced planning tools, enabling accurate forecasting, resource allocation, and scheduling." },
    { title: "Customizable Reports and Analytics", description: "Generate detailed reports and analytics on production, inventory, sales, and costs to support data-driven decision-making and performance monitoring." },
    { title: "Multi-Location Support", description: "Manage production across multiple factories or locations from a centralized dashboard, ensuring consistent operations and easy scalability." },
    { title: "GST Compliance", description: "Ensure full GST compliance with support for automated e-invoicing, e-way billing, and seamless GST reconciliation, simplifying tax reporting and reducing compliance risks." }
  ];

  return (
    <>
      <Header />
      <section className="about-title-section">
        <h2><img alt="quantologo" src={logo} style={{ width: 280, marginLeft: 10, verticalAlign: "middle", marginBottom: 20 }} />Manufacturing</h2>
      </section>
      <div ref={containerRef} className="container-mfg">
        <div className="background-pattern-mfg"></div>
        <motion.div
          className="radial-gradient-mfg"
          style={{
            scale: useTransform(scrollYProgress, [0, 1], [1, 5]),
            opacity: useTransform(scrollYProgress, [0, 0.5, 1], [0.2, 0.1, 0]),
          }}
        />
        <div className="content-mfg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center-mfg"
          >
            <h1 className="heading-mfg">
              Quanto Mfg: The Ultimate ERP Solution
            </h1>
            <p className="subheading-mfg">
              Revolutionizing textile manufacturing with comprehensive management solutions.
            </p>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
            >
              <ArrowDown size={48} className="arrow-mfg" />
              <p className="explore-text-mfg">Explore the benefits</p>
            </motion.div>
          </motion.div>
        </div>

        <div className="benefits-section-mfg">
          <div className="benefits-container-mfg">
            <h2 className="benefits-heading-mfg">
              Key Benefits of Quanto Mfg
            </h2>
            <div className="benefits-grid-mfg">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="benefit-card-mfg"
                  onMouseEnter={() => setHoveredBenefit(index)}
                  onMouseLeave={() => setHoveredBenefit(null)}
                >
                  <div className="benefit-content-mfg">
                    <CheckCircle className="benefit-icon-mfg" />
                    <h4 className="benefit-title-mfg">{benefit.title}</h4>
                    <p className="benefit-description-mfg">{benefit.description}</p>
                  </div>
                  <motion.div
                    className="hover-overlay-mfg"
                    style={{
                      opacity: hoveredBenefit === index ? 0.15 : 0,
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <motion.div
          style={{
            opacity: useTransform(scrollYProgress, [0.8, 0.9], [0, 1]),
            y: useTransform(scrollYProgress, [0.8, 0.9], [50, 0])
          }}
          className="cta-section-mfg"
        >
          <div className="cta-content-mfg">
            <h2 className="cta-heading-mfg">
              Why Choose Quanto Mfg?
            </h2>
            <p className="cta-description-mfg">
              Quanto Mfg equips textile manufacturers with cutting-edge tools to streamline operations, cut costs, and elevate product quality.
            </p>
          </div>
        </motion.div>
      </div>
      <Footer />
    </>
  );
}
